import { getAuthHeaders } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

/**
 * Extract a human-readable error message from a FastAPI error response body.
 * FastAPI can return `detail` as a string (HTTPException) or as an array of
 * validation error objects (RequestValidationError).  Passing either form to
 * `new Error()` directly turns arrays into "[object Object]", so we normalise
 * here.
 */
function extractErrorMessage(errorBody, fallback) {
  const detail = errorBody?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || fallback;
  }
  return fallback;
}

/**
 * Super Admin API Service
 * All endpoints require MANAGE_USERS permission (Super Admin role)
 */

/**
 * Get comprehensive dashboard overview
 * @returns {Promise<Object>} Dashboard data with all statistics
 */
export async function fetchSuperAdminDashboard() {
  const response = await fetch(`${API_BASE_URL}/super-admin/dashboard`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch dashboard data"));
  }

  return await response.json();
}

/**
 * Get all users with optional filtering
 * @param {Object} params - Query parameters
 * @param {number} params.skip - Pagination offset
 * @param {number} params.limit - Maximum records
 * @param {string} params.role - Filter by role
 * @param {string} params.status - Filter by status
 * @param {string} params.search - Search by email/name
 * @returns {Promise<Array>} List of users
 */
export async function fetchUsers(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.skip !== undefined) queryParams.append('skip', params.skip);
  if (params.limit !== undefined) queryParams.append('limit', params.limit);
  if (params.role) queryParams.append('role', params.role);
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);

  const response = await fetch(`${API_BASE_URL}/super-admin/users?${queryParams}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch users"));
  }

  return await response.json();
}

/**
 * Get total user count with filters
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search by email/name
 * @param {string} params.role - Filter by role
 * @param {string} params.status - Filter by status
 * @returns {Promise<number>} Total user count
 */
export async function fetchUserCount(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.search) queryParams.append('search', params.search);
  if (params.role) queryParams.append('role', params.role);
  if (params.status) queryParams.append('status', params.status);

  const response = await fetch(`${API_BASE_URL}/super-admin/users/count?${queryParams}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch user count"));
  }

  const data = await response.json();
  return data.total_count;
}

/**
 * Get user statistics
 * @returns {Promise<Object>} User statistics
 */
export async function fetchUserStatistics() {
  const response = await fetch(`${API_BASE_URL}/super-admin/users/statistics`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch user statistics"));
  }

  return await response.json();
}

/**
 * Get all roles
 * @returns {Promise<Array>} List of roles
 */
export async function fetchRoles() {
  const response = await fetch(`${API_BASE_URL}/super-admin/roles`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch roles"));
  }

  return await response.json();
}

/**
 * Update user status (enable/disable)
 * @param {string} userId - User ID
 * @param {string} status - New status (ACTIVE/DISABLED)
 * @returns {Promise<Object>} Update result
 */
export async function updateUserStatus(userId, status) {
  const response = await fetch(`${API_BASE_URL}/super-admin/users/${userId}/status`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include',
    body: JSON.stringify({
      status: status  // Only send status, user_id is in URL path
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to update user status"));
  }

  return await response.json();
}

/**
 * Delete a user
 * @param {string} userId - User ID
 * @param {string} reason - Optional reason for deletion
 * @returns {Promise<Object>} Delete result
 */
export async function deleteUser(userId, reason = null) {
  const url = new URL(`${API_BASE_URL}/super-admin/users/${userId}`, window.location.origin);
  if (reason) url.searchParams.append('reason', reason);

  const response = await fetch(url.toString(), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to delete user"));
  }

  return await response.json();
}

/**
 * Perform bulk action on users
 * @param {Array<string>} userIds - Array of user IDs
 * @param {string} action - Action to perform (enable/disable/delete)
 * @param {string} reason - Reason for action
 * @returns {Promise<Object>} Bulk action result
 */
export async function bulkUserAction(userIds, action, reason = '') {
  const response = await fetch(`${API_BASE_URL}/super-admin/users/bulk-action`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include',
    body: JSON.stringify({
      user_ids: userIds,
      action: action,  // "enable", "disable", or "delete"
      reason: reason
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to perform bulk action"));
  }

  return await response.json();
}

/**
 * Get campaign statistics
 * @returns {Promise<Object>} Campaign statistics
 */
export async function fetchCampaignStatistics() {
  const response = await fetch(`${API_BASE_URL}/super-admin/campaigns/statistics`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch campaign statistics"));
  }

  return await response.json();
}

/**
 * Get recent campaigns
 * @param {number} limit - Number of campaigns to fetch
 * @returns {Promise<Array>} Recent campaigns
 */
export async function fetchRecentCampaigns(limit = 10) {
  const response = await fetch(`${API_BASE_URL}/super-admin/campaigns/recent?limit=${limit}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch recent campaigns"));
  }

  return await response.json();
}

/**
 * Get database statistics
 * @returns {Promise<Object>} Database statistics
 */
export async function fetchDatabaseStatistics() {
  const response = await fetch(`${API_BASE_URL}/super-admin/database/statistics`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch database statistics"));
  }

  return await response.json();
}

/**
 * Get recent activities
 * @param {number} limit - Number of activities to fetch
 * @returns {Promise<Array>} Recent activities
 */
export async function fetchRecentActivities(limit = 20) {
  const response = await fetch(`${API_BASE_URL}/super-admin/activities/recent?limit=${limit}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch recent activities"));
  }

  return await response.json();
}

/**
 * Ingest Campaign Result data from Excel or CSV file
 * @param {File} file - Excel (.xlsx, .xls) or CSV (.csv) file
 * @param {string} scheduleTime - Optional ISO 8601 timestamp for scheduled ingestion
 * @returns {Promise<Object>} Ingestion result with statistics
 */
export async function ingestCampaignResultData(file, scheduleTime = null) {
  const formData = new FormData();
  formData.append('file', file);
  
  if (scheduleTime) {
    formData.append('schedule_time', scheduleTime);
  }
  
  const response = await fetch(`${API_BASE_URL}/super-admin/ingest/campaign-result`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders()
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    credentials: 'include',
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to ingest campaign result data"));
  }

  return await response.json();
}

/**
 * Ingest ROI data from Excel or CSV file
 * @param {File} file - Excel (.xlsx, .xls) or CSV (.csv) file
 * @param {string} scheduleTime - Optional ISO 8601 timestamp for scheduled ingestion
 * @returns {Promise<Object>} Ingestion result with statistics
 */
export async function ingestROIData(file, scheduleTime = null) {
  const formData = new FormData();
  formData.append('file', file);
  
  if (scheduleTime) {
    formData.append('schedule_time', scheduleTime);
  }
  
  const response = await fetch(`${API_BASE_URL}/super-admin/ingest/roi`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders()
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    credentials: 'include',
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to ingest ROI data"));
  }

  return await response.json();
}

// ============================================================================
// POC Management API Functions
// ============================================================================

/**
 * Get POC statistics
 * @returns {Promise<Object>} POC statistics including totals by region and country
 */
export async function fetchPOCStatistics() {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/statistics`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch POC statistics"));
  }

  return await response.json();
}

/**
 * Check if a POC exists
 * @param {string} pocId - POC ID to check
 * @returns {Promise<Object>} Existence information and POC details if found
 */
export async function checkPOCExists(pocId) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/check/${pocId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to check POC existence"));
  }

  return await response.json();
}

/**
 * Get all POCs with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.search - Search by ID, name, or city
 * @param {string} params.region - Filter by region
 * @param {string} params.country - Filter by country
 * @returns {Promise<Object>} Paginated POC list
 */
export async function fetchPOCs(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.search) queryParams.append('search', params.search);
  if (params.region) queryParams.append('region', params.region);
  if (params.country) queryParams.append('country', params.country);

  const response = await fetch(`${API_BASE_URL}/super-admin/pocs?${queryParams}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch POCs"));
  }

  return await response.json();
}

/**
 * Get all Amplify POCs with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.search - Search by ID, name, or city
 * @param {string} params.region - Filter by region
 * @param {string} params.country - Filter by country
 * @returns {Promise<Object>} Paginated Amplify POC list
 */
export async function fetchAmplifyPOCs(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.search) queryParams.append('search', params.search);
  if (params.region) queryParams.append('region', params.region);
  if (params.country) queryParams.append('country', params.country);

  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/amplify?${queryParams}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to fetch Amplify POCs"));
  }

  return await response.json();
}

/**
 * Get POC by ID
 * @param {string} pocId - POC ID
 * @returns {Promise<Object>} POC details
 */
export async function fetchPOCById(pocId) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/${pocId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, `POC with ID ${pocId} not found`));
  }

  return await response.json();
}

/**
 * Get Amplify POC by ID
 * @param {string} pocId - POC ID
 * @returns {Promise<Object>} Amplify POC details
 */
export async function fetchAmplifyPOCById(pocId) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/amplify/${pocId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, `Amplify POC with ID ${pocId} not found`));
  }

  return await response.json();
}

/**
 * Create a new POC
 * @param {Object} pocData - POC data
 * @returns {Promise<Object>} Created POC
 */
export async function createPOC(pocData) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include',
    body: JSON.stringify(pocData)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to create POC"));
  }

  return await response.json();
}

/**
 * Create a new Amplify POC
 * @param {Object} pocData - Amplify POC data
 * @param {boolean} pocData.add_to_poc_table - Whether to also create in POC table
 * @returns {Promise<Object>} Created Amplify POC
 */
export async function createAmplifyPOC(pocData) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/amplify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include',
    body: JSON.stringify(pocData)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to create Amplify POC"));
  }

  return await response.json();
}

/**
 * Update a POC
 * @param {string} pocId - POC ID
 * @param {Object} pocData - Updated POC data
 * @returns {Promise<Object>} Updated POC
 */
export async function updatePOC(pocId, pocData) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/${pocId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include',
    body: JSON.stringify(pocData)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to update POC"));
  }

  return await response.json();
}

/**
 * Update an Amplify POC
 * @param {string} pocId - POC ID
 * @param {Object} pocData - Updated Amplify POC data
 * @returns {Promise<Object>} Updated Amplify POC
 */
export async function updateAmplifyPOC(pocId, pocData) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/amplify/${pocId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include',
    body: JSON.stringify(pocData)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to update Amplify POC"));
  }

  return await response.json();
}

/**
 * Delete a POC
 * @param {string} pocId - POC ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deletePOC(pocId) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/${pocId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to delete POC"));
  }

  return await response.json();
}

/**
 * Delete an Amplify POC
 * @param {string} pocId - POC ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteAmplifyPOC(pocId) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/amplify/${pocId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to delete Amplify POC"));
  }

  return await response.json();
}

/**
 * Sync a POC to Amplify POC table
 * @param {string} pocId - POC ID
 * @param {number} traditionalBudget - Traditional budget to assign
 * @returns {Promise<Object>} Synced Amplify POC
 */
export async function syncPOCToAmplify(pocId, traditionalBudget = 0.0) {
  const response = await fetch(`${API_BASE_URL}/super-admin/pocs/${pocId}/sync-to-amplify?traditional_budget=${traditionalBudget}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, "Failed to sync POC to Amplify"));
  }

  return await response.json();
}